
import React from "react";
import { Site } from "@/services/types";
import SiteInfoCard from "./SiteInfoCard";
import SiteDescriptionCard from "./SiteDescriptionCard";
import SiteLoadingState from "./SiteLoadingState";
import SiteNotFound from "./SiteNotFound";
import SiteGoogleMap from "./SiteGoogleMap";
import StructuredData from "@/components/StructuredData";
import ServiceAreaSection from "./ServiceAreaSection";
import GoogleReviews from "./GoogleReviews";
import RecentReviews from "@/components/RecentReviews";

interface SiteDetailContentProps {
  loading: boolean;
  site: Site | null;
  formatOpeningHours: (site: Site) => string;
  getDirectionsUrl: (site: Site) => string;
  getSiteDescription: (site: Site) => React.ReactNode;
  isMiniSite?: boolean;
}

const SiteDetailContent: React.FC<SiteDetailContentProps> = ({
  loading,
  site,
  formatOpeningHours,
  getDirectionsUrl,
  getSiteDescription,
  isMiniSite = false,
}) => {
  // Génération des données structurées pour ce site
  const generateSiteStructuredData = (site: Site) => {
    if (!site) return null;
    
    // Format des horaires d'ouverture pour Schema.org
    const formatOpeningHoursSpecification = (site: Site) => {
      if (!site.opening_hours) return [];
      
      const daysMapping: Record<string, string> = {
        lundi: "Monday",
        mardi: "Tuesday",
        mercredi: "Wednesday",
        jeudi: "Thursday",
        vendredi: "Friday",
        samedi: "Saturday",
        dimanche: "Sunday"
      };
      
      const specifications: any[] = [];
      
      Object.entries(site.opening_hours).forEach(([day, hours]) => {
        if (!hours) return;
        
        const englishDay = daysMapping[day];
        if (!englishDay) return;
        
        // Matin
        if (hours.matin && hours.matin !== "Fermé") {
          const [opens, closes] = hours.matin.split('-');
          if (opens && closes) {
            specifications.push({
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": englishDay,
              "opens": opens.replace('h', ':') + (opens.includes(':') ? '' : ':00'),
              "closes": closes.replace('h', ':') + (closes.includes(':') ? '' : ':00')
            });
          }
        }
        
        // Après-midi
        if (hours.apres_midi && hours.apres_midi !== "Fermé") {
          const [opens, closes] = hours.apres_midi.split('-');
          if (opens && closes) {
            specifications.push({
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": englishDay,
              "opens": opens.replace('h', ':') + (opens.includes(':') ? '' : ':00'),
              "closes": closes.replace('h', ':') + (closes.includes(':') ? '' : ':00')
            });
          }
        }
      });
      
      return specifications;
    };
    
    // Construction des données structurées
    return {
      "@context": "https://schema.org",
      "@type": "RecyclingCenter",
      "name": site.name,
      "description": site.description || `Centre de recyclage et valorisation ${site.name} situé à ${site.city}`,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": site.address || "",
        "addressLocality": site.city || "",
        "postalCode": site.postalCode || "",
        "addressCountry": "FR"
      },
      "telephone": site.phone || "",
      "email": site.email || "",
      "url": site.websiteUrl || "",
      "openingHoursSpecification": formatOpeningHoursSpecification(site),
      "geo": site.coordinates ? {
        "@type": "GeoCoordinates",
        "latitude": site.coordinates.lat,
        "longitude": site.coordinates.lng
      } : undefined,
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": site.coordinates ? {
          "@type": "GeoCoordinates",
          "latitude": site.coordinates.lat,
          "longitude": site.coordinates.lng
        } : undefined,
        "geoRadius": "30000"
      },
      "memberOf": {
        "@type": "Organization",
        "name": "Groupe Fabre Environnement",
        "url": "https://groupefabre.com/"
      },
      "makesOffer": site.services?.map(service => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service
        }
      }))
    };
  };

  return (
    <>
      <main className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <SiteLoadingState />
          ) : site ? (
            <>
              {/* Données structurées pour ce site */}
              <StructuredData data={generateSiteStructuredData(site)} />
              
              {/* Section principale - H2 */}
              <section className="mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-fabre-gray mb-8 text-center">
                  Informations du centre
                </h2>
                
                {isMiniSite ? (
                  // Layout simplifié pour les mini-sites
                  <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                      <SiteInfoCard 
                        site={site} 
                        formatOpeningHours={formatOpeningHours}
                        getDirectionsUrl={getDirectionsUrl}
                        isMiniSite={true}
                      />
                      <div>
                        <h3 className="text-2xl font-heading font-semibold text-fabre-darkteal mb-4">
                          Localisation
                        </h3>
                        <SiteGoogleMap site={site} />
                      </div>
                    </div>
                  </div>
                ) : (
                  // Layout complet pour les sites normaux
                  <div className="max-w-7xl mx-auto">
                    {/* Première ligne : Info + Carte */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                      <SiteInfoCard 
                        site={site} 
                        formatOpeningHours={formatOpeningHours}
                        getDirectionsUrl={getDirectionsUrl} 
                      />
                      <div>
                        <h3 className="text-2xl font-heading font-semibold text-fabre-darkteal mb-4">
                          Localisation et itinéraire
                        </h3>
                        <SiteGoogleMap site={site} />
                      </div>
                    </div>
                  </div>
                )}
              </section>
              
              {/* Section description - H2 */}
              <section className="mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-fabre-gray mb-8 text-center">
                  À propos de ce centre
                </h2>
                <article className={isMiniSite ? "max-w-6xl mx-auto" : "max-w-7xl mx-auto"}>
                  <SiteDescriptionCard 
                    site={site} 
                    getSiteDescription={getSiteDescription} 
                  />
                </article>
              </section>

              {/* Section avis Google */}
              <section className="mb-12">
                <div className={isMiniSite ? "max-w-6xl mx-auto" : "max-w-7xl mx-auto"}>
                  <GoogleReviews 
                    siteName={site.name}
                    address={site.address}
                    city={site.city}
                  />
                </div>
              </section>
            </>
          ) : (
            <SiteNotFound />
          )}
        </div>
      </main>
      
      {/* Avis clients récents globaux */}
      {isMiniSite && (
        <RecentReviews 
          title="Témoignages de nos autres clients"
          maxReviews={3}
        />
      )}
      
      {/* Zone de chalandise - Section avec H2 */}
      {site && (
        <section className="bg-fabre-lightgray/30 py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-fabre-gray mb-8 text-center">
              Zone d'intervention
            </h2>
            <ServiceAreaSection 
              siteName={site.name} 
              city={site.city} 
            />
          </div>
        </section>
      )}
    </>
  );
};

export default SiteDetailContent;
